import { FC, useMemo, useCallback } from 'react'
import { CreditCardPagePreset } from 'components/features/creditCard/creditCardPage/CreditCardPagePreset/CreditCardPagePreset'
import { CardNeverAppliedPreset } from 'components/features/creditCard/creditCardComparisonChart/CardNeverAppliedPreset/CardNeverAppliedPreset'
import styles from 'components/features/creditCard/creditCardPage/CardApplicationPage/CardApplicationPage.styl'
import Button from 'components/features/pfm/Button/Button'
import { Link } from 'react-router-dom'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import ModuleLayout from 'components/atoms/ModuleLayout/ModuleLayout'
import Flex from 'components/atoms/Flex/Flex'
import Separator from 'components/atoms/Separator/Separator'
import { CheckingEligibilityContent } from 'components/features/creditCard/creditCardPage/CheckingEligibilityContent/CheckingEligibilityContent'
import { EligibilityFailedContent } from 'components/features/creditCard/creditCardPage/EligibilityFailedContent/EligibilityFailedContent'
import {
  CC_ONBOARDING_STATE_CHECKING_ELIGIBILITY,
  CC_ONBOARDING_STATE_FAILED,
  CC_ONBOARDING_STATE_NOT_STARTED,
  CC_ONBOARDING_STATE_OFFER_RECEIVED,
  ECA_DOCUMENT_PDF_URL,
} from 'components/features/creditCard/creditCardStore/constants'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { CardStatus } from 'components/features/creditCard/creditCardPage/CardStatus/CardStatus'
import { JoinCCWaitlistAction, PatchCCOnboardingPropsAction } from 'components/features/creditCard/creditCardStore/actions'
import { featureFlagSelector, memberInfoSelector } from 'selectors/globalSelectors'
import { IS_MOBILE } from 'helpers/userAgentHelpers'
import { waitlistRequestSelector } from 'components/features/creditCard/creditCardStore/selectors'

export const CardApplicationPage: FC = () => {
  const isCardFeatureEnabled = useStoreSelector((state) => featureFlagSelector(state, 'enable_credit_card'))
  const memberInfo = useStoreSelector(memberInfoSelector)
  const waitlistRequest = useStoreSelector(waitlistRequestSelector)
  const onboarding = useStoreSelector((state) => state.data.creditCard.onboarding)
  const applicationNotStarted = onboarding?.state === CC_ONBOARDING_STATE_NOT_STARTED
  const offerReceived = onboarding?.state === CC_ONBOARDING_STATE_OFFER_RECEIVED
  const offerDenied = onboarding?.state === CC_ONBOARDING_STATE_FAILED
  const offerPending = onboarding?.state === CC_ONBOARDING_STATE_CHECKING_ELIGIBILITY

  const applicationResultChild = useMemo(() => {
    if (offerPending || offerReceived) {
      return <CheckingEligibilityContent />
    } else if (offerDenied) {
      return <EligibilityFailedContent />
    }
    return null
  }, [offerDenied, offerPending, offerReceived])

  const dispatch = useStoreDispatch()
  const joinWaitlist = useCallback(() => {
    dispatch(new JoinCCWaitlistAction())
  }, [dispatch])

  const reconsiderOffer = useCallback(() => {
    dispatch(new PatchCCOnboardingPropsAction({
      step: 'offer',
    }))
  }, [dispatch])

  const seeOfferButtonChild = useMemo(() => offerReceived && (
    <Button
      text='See Your Card Offer'
      onClick={ reconsiderOffer }
      qaMarker='see-offer-button'
    />
  ), [offerReceived, reconsiderOffer])


  const ctaText = useMemo(() => {
    if (offerReceived || offerPending || offerDenied) {
      return 'See Your Application'
    } else if (applicationNotStarted) {
      return 'Apply Now'
    } else {
      return 'Continue Your Application'
    }
  }, [applicationNotStarted, offerDenied, offerPending, offerReceived])

  const applyNowChild = useMemo(() => {
    if (!isCardFeatureEnabled) {
      return memberInfo?.has_joined_card_waitlist ? (
        <Flex theme='lightGreen' component='p' padding='small' gap='none'>
          <strong>You're on the waitlist. </strong>
          You'll be the first to know when the card is available.
        </Flex>
      ) : (
        <Button text='Join the Waitlist' onClick={ joinWaitlist } fullWidth={ IS_MOBILE } isSubmitting={ waitlistRequest.isLoading } />
      )
    } else {
      return (
        <Link to='/credit-card/onboarding'>
          <Button text={ ctaText } />
        </Link>
      )
    }
  }, [isCardFeatureEnabled, memberInfo?.has_joined_card_waitlist, joinWaitlist, waitlistRequest.isLoading, ctaText])


  const neverAppliedContent = (
    <>
      { !isCardFeatureEnabled && <h5>COMING SOON</h5> }
      <h1>
        Unlimited 2% cash back on every purchase
      </h1>
      <p>
        Redeem for cash or invest your rewards in Bitcoin so they can increase in value. No annual fee or foreign transaction fees.
      </p>
      { applyNowChild }
    </>
  )

  const cardBenefits = useMemo(() => !offerDenied && (
    <>
      <ModuleLayout padding='medium' marginBottom='huge'>
        <Flex className={ styles.blurbs } component='ul' horizontal responsive>
          <li>
            <h2>
              2% cash back
            </h2>
            <h4>
              on every purchase, redeem for cash or invest your rewards in Bitcoin
            </h4>
          </li>
          <Separator vertical />
          <li>
            <h2>
              Free Premium
            </h2>
            <h4>
              as long as your credit card account is open, a $200 value
            </h4>
          </li>
          <Separator vertical />
          <li>
            <h2>
              No annual fee
            </h2>
            <h4>
              no foreign transaction fees, and no fees to purchase Bitcoin
            </h4>
          </li>
        </Flex>
      </ModuleLayout>

      <ModuleLayout horizontal responsive gap='medium' padding='responsive' marginBottom='huge' horizontallyCenter className={ styles.chart }>
        <CardNeverAppliedPreset />
      </ModuleLayout>

      <div className={ styles.footnotes }>
        <div className={ styles.applyButton }>
          { seeOfferButtonChild || (!offerPending && applyNowChild) }
        </div>
        <div className={ styles.centerNote }>
          Status Money is a financial management company, not a bank. Card is issued by Evolve Bank &amp; Trust, Member FDIC pursuant to a license from Visa&trade;.
        </div>
        <div className={ styles.notes }>
          * Rewards value calculated assuming a 2% earn rate and the monthly card spending and crypto performance values above.
          Bitcoin's price increased by an average of 10% per month over the 5 years ending May 2021 and 15% during the year ending May 2021.
          Past performance does not guarantee future investment returns. Your returns will be based on market performance.
          To calculate Citi Double Cash rewards, we assume a 60% average payment rate.
          To calculate Chase Freedom Unlimited rewards, we assume 6% of spending is at restaurants and 3% is on travel (based on BLS report dated Sept 9, 2020).
        </div>
        <div className={ styles.links }>
          <NavigationLink to='terms'>Terms & Conditions</NavigationLink>
          <NavigationLink to='privacy'>Privacy Policy</NavigationLink>
          <a href={ ECA_DOCUMENT_PDF_URL } target='_blank'>Electronic Communications Agreement</a>
        </div>
      </div>
    </>
  ), [applyNowChild, offerDenied, seeOfferButtonChild])

  const title = isCardFeatureEnabled ? 'Your Application' : null
  return (
    <CreditCardPagePreset title='The Status Credit Card'>
      <ModuleLayout title={ title } horizontal responsive gap='medium' padding='medium' marginBottom='huge'>
        <Flex gap='medium' padding='small' rounded className={ styles.application }>
          { applicationResultChild || neverAppliedContent }
        </Flex>
        <Flex>
          <CardStatus />
        </Flex>
      </ModuleLayout>
      { cardBenefits}
    </CreditCardPagePreset>
  )
}
